import moment from 'moment/moment';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import {
  AbsenceEntry,
  AbsenceStatus,
  AbsenceType,
} from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { EditAbsenceDialogProps } from '~/features/AbsenceChanges/EditAbsenceDialog/component';
import { useAbsencePermissions } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/useAbsencePermissions';
import { isDateWithinRange } from '~/utils/helpers/date';

type DialogStateType<S> = [S, Dispatch<SetStateAction<S>>];

export interface UseMyAbsencesOutput {
  employeeId: number;
  absences: AbsenceEntry[];
  permissions: {
    canAddAbsence: boolean;
    canEditSelectedAbsence: (absenceId: string) => boolean;
  };
  dialogStates: {
    edit: DialogStateType<Pick<EditAbsenceDialogProps, 'open' | 'absenceId'>>;
    delete: DialogStateType<string | undefined>;
  };
  isLoading: boolean;
  failedToLoad: boolean;
}

export const useMyAbsences = (): UseMyAbsencesOutput => {
  const loggedInUserId = useLoggedInEmployeeId();
  const {
    absenceEntries,
    loading: absenceDataLoading,
    failedToLoad: absenceDataFailedToLoad,
  } = useEmployeeAbsenceData({
    employeeId: loggedInUserId,
  });

  const entries = useMemo(() => {
    // Settings: What range do we show absences on widget
    const widgetAbsencesRange = {
      startDate: moment().startOf('day').toDate(),
      endDate: moment().add(3, 'month').startOf('day').toDate(),
    };

    const isDateInRange = (date?: Date) =>
      !!date &&
      isDateWithinRange(
        date,
        widgetAbsencesRange.startDate,
        widgetAbsencesRange.endDate,
      );
    const isContinuingLongAbsence = (entry: AbsenceEntry) =>
      entry.type === AbsenceType.LONG &&
      !entry.endDate &&
      moment(entry.startDate).isBefore(widgetAbsencesRange.startDate);

    return absenceEntries.filter((a) => {
      const excludeAlways = [AbsenceStatus.REJECTED].includes(a.status);
      if (excludeAlways) {
        return false;
      }

      const includeAlways = [
        AbsenceStatus.PROPOSED,
        AbsenceStatus.CHANGES_REQUESTED,
      ].includes(a.status);
      if (includeAlways) {
        return true;
      }

      const includeInRange = [AbsenceStatus.APPROVED].includes(a.status);
      const isInRange =
        isDateInRange(a.startDate) ||
        isDateInRange(a.endDate) ||
        isContinuingLongAbsence(a);

      return includeInRange && isInRange;
    });
  }, [absenceEntries]);

  const { canAddAbsence, canEditSelectedAbsence } = useAbsencePermissions({
    employeeId: loggedInUserId,
  });

  const [editDialogState, setEditDialogState] = useState<
    Pick<EditAbsenceDialogProps, 'open' | 'absenceId'>
  >({
    open: false,
  });
  const [deleteAbsenceId, setDeleteAbsenceId] = useState<string | undefined>(
    undefined,
  );

  return {
    employeeId: loggedInUserId,
    absences: entries,
    permissions: {
      canAddAbsence,
      canEditSelectedAbsence,
    },
    dialogStates: {
      edit: [editDialogState, setEditDialogState],
      delete: [deleteAbsenceId, setDeleteAbsenceId],
    },
    isLoading: absenceDataLoading,
    failedToLoad: absenceDataFailedToLoad,
  };
};
